import { useEffect, useState } from "react";
import { Outlet, NavLink, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  House, CalendarBlank, MapTrifold, Receipt, UsersThree, SignOut, Gear,
  ArrowSquareOut, Image, MagnifyingGlass, CreditCard, EnvelopeSimple, Article,
  PaperPlaneTilt, EnvelopeOpen, ChartBar, Gift, Bell, Repeat, CurrencyDollar,
  Storefront, List, X,
} from "@phosphor-icons/react";

export default function AdminLayout() {
  const { email, loading, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  // Close drawer on route change
  useEffect(() => { setOpen(false); }, [loc.pathname, loc.hash]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-ms-stone">…</div>;
  if (!email) return <Navigate to="/admin/login" replace />;

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 md:py-2.5 rounded-lg text-sm transition-all ${
      isActive ? "bg-ms-earth text-ms-cream" : "text-ms-stone hover:bg-ms-edge/40"
    }`;

  const settingsAnchor = (anchor) => () => {
    const onPage = loc.pathname === "/admin/settings";
    const hashMatch = loc.hash === `#${anchor}`;
    const isActive = onPage && (hashMatch || (anchor === "homepage" && !loc.hash));
    return `flex items-center gap-3 px-4 py-3 md:py-2.5 rounded-lg text-sm transition-all ${
      isActive ? "bg-ms-earth text-ms-cream" : "text-ms-stone hover:bg-ms-edge/40"
    }`;
  };

  return (
    <div className="min-h-screen bg-ms-cream md:flex">
      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-30 flex items-center justify-between bg-white border-b border-ms-edge px-4 py-3" data-testid="admin-mobile-topbar">
        <button
          onClick={() => setOpen(true)}
          className="p-2 -ml-2 text-ms-earth"
          aria-label="Open menu"
          data-testid="admin-mobile-menu-toggle"
        >
          <List size={24} weight="bold" />
        </button>
        <p className="serif text-lg text-ms-earth">Mystery <em className="text-ms-brass">Served</em></p>
        <a href="/" target="_blank" rel="noreferrer" className="p-2 -mr-2 text-ms-stone" aria-label="View public site">
          <ArrowSquareOut size={20} weight="duotone" />
        </a>
      </header>

      {/* Backdrop (mobile only) */}
      {open && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setOpen(false)}
          data-testid="admin-mobile-backdrop"
        />
      )}

      {/* Sidebar — fixed drawer on mobile, static on desktop */}
      <aside
        className={`
          fixed md:static top-0 left-0 z-50 h-full w-72 md:w-64
          bg-white border-r border-ms-edge flex flex-col
          transform transition-transform duration-200
          ${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0
        `}
        data-testid="admin-sidebar"
      >
        <div className="p-6 border-b border-ms-edge flex items-start justify-between">
          <div>
            <p className="serif text-2xl text-ms-earth">Mystery <em className="text-ms-brass">Served</em></p>
            <p className="label-eyebrow text-ms-stone mt-1">Operator</p>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="md:hidden p-1 -mr-1 text-ms-stone hover:text-ms-earth"
            aria-label="Close menu"
            data-testid="admin-mobile-menu-close"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-5 overflow-y-auto">
          <div className="space-y-1">
            <NavLink to="/admin" end className={linkClass} data-testid="nav-dashboard">
              <House size={18} weight="duotone" /> Dashboard
            </NavLink>
            <a href="/" target="_blank" rel="noreferrer"
              className="hidden md:flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-ms-stone hover:bg-ms-edge/40 transition-all"
              data-testid="nav-public-site">
              <ArrowSquareOut size={18} weight="duotone" /> View public site
            </a>
          </div>

          <SidebarGroup label="Website">
            <NavLink to="/admin/settings#homepage" className={settingsAnchor("homepage")} data-testid="nav-homepage-editor"><Image size={18} weight="duotone" /> Homepage / Hero</NavLink>
            <NavLink to="/admin/settings#about" className={settingsAnchor("about")} data-testid="nav-about-editor"><Article size={18} weight="duotone" /> About page</NavLink>
            <NavLink to="/admin/settings#footer" className={settingsAnchor("footer")} data-testid="nav-footer-editor"><Article size={18} weight="duotone" /> Footer</NavLink>
            <NavLink to="/admin/settings#seo" className={settingsAnchor("seo")} data-testid="nav-seo-settings"><MagnifyingGlass size={18} weight="duotone" /> SEO</NavLink>
          </SidebarGroup>

          <SidebarGroup label="Bookings">
            <NavLink to="/admin/tours" className={linkClass} data-testid="nav-admin-tours"><MapTrifold size={18} weight="duotone" /> Tours</NavLink>
            <NavLink to="/admin/calendar" className={linkClass} data-testid="nav-admin-calendar"><CalendarBlank size={18} weight="duotone" /> Tour dates</NavLink>
            <NavLink to="/admin/schedule-rules" className={linkClass} data-testid="nav-admin-schedule-rules"><Repeat size={18} weight="duotone" /> Recurring rules</NavLink>
            <NavLink to="/admin/bookings" className={linkClass} data-testid="nav-admin-bookings"><Receipt size={18} weight="duotone" /> Bookings</NavLink>
            <NavLink to="/admin/waitlist" className={linkClass} data-testid="nav-admin-waitlist"><Bell size={18} weight="duotone" /> Waitlist</NavLink>
            <NavLink to="/admin/gift-cards" className={linkClass} data-testid="nav-admin-gift-cards"><Gift size={18} weight="duotone" /> Gift cards</NavLink>
            <NavLink to="/admin/customers" className={linkClass} data-testid="nav-admin-customers"><UsersThree size={18} weight="duotone" /> Customers</NavLink>
            <NavLink to="/admin/stats" className={linkClass} data-testid="nav-admin-stats"><ChartBar size={18} weight="duotone" /> Stats</NavLink>
            <NavLink to="/admin/bookkeeping" className={linkClass} data-testid="nav-admin-bookkeeping"><CurrencyDollar size={18} weight="duotone" /> Bookkeeping</NavLink>
            <NavLink to="/admin/restaurant-notifications" className={linkClass} data-testid="nav-admin-restaurant-notifications"><Storefront size={18} weight="duotone" /> Restaurant alerts</NavLink>
            <NavLink to="/admin/subscribers" className={linkClass} data-testid="nav-admin-subscribers"><EnvelopeSimple size={18} weight="duotone" /> Subscribers</NavLink>
            <NavLink to="/admin/broadcasts" className={linkClass} data-testid="nav-admin-broadcasts"><PaperPlaneTilt size={18} weight="duotone" /> Broadcasts</NavLink>
          </SidebarGroup>

          <SidebarGroup label="System">
            <NavLink to="/admin/settings#payments" className={settingsAnchor("payments")} data-testid="nav-payments"><CreditCard size={18} weight="duotone" /> Payments / Stripe</NavLink>
            <NavLink to="/admin/settings#email" className={settingsAnchor("email")} data-testid="nav-email"><EnvelopeSimple size={18} weight="duotone" /> Email</NavLink>
            <NavLink to="/admin/email-templates" className={linkClass} data-testid="nav-email-templates"><EnvelopeOpen size={18} weight="duotone" /> Email Templates</NavLink>
            <NavLink to="/admin/settings" end className={linkClass} data-testid="nav-admin-settings"><Gear size={18} weight="duotone" /> All settings</NavLink>
          </SidebarGroup>
        </nav>

        <div className="p-4 border-t border-ms-edge">
          <p className="text-xs text-ms-stone mb-2 truncate">{email}</p>
          <button onClick={() => { logout(); nav("/admin/login"); }} className="flex items-center gap-2 text-sm text-ms-stone hover:text-ms-danger" data-testid="admin-logout">
            <SignOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto min-w-0">
        <Outlet />
      </main>
    </div>
  );
}

function SidebarGroup({ label, children }) {
  return (
    <div>
      <p className="label-eyebrow text-ms-stone text-[10px] px-4 mb-2">{label}</p>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
